import { useMemo } from 'react';
import { TokenData } from '@/types';

/**
 * Utility functions for performance optimizations
 */

/**
 * Memoized balance calculation for wallet tokens
 */
export const useCalculatedBalance = (tokens: TokenData[]) => {
  return useMemo(() => {
    const totalBalance = tokens.reduce((sum, token) => {
      const value = parseFloat(token.usdValue.replace(/,/g, ''));
      return sum + (isNaN(value) ? 0 : value);
    }, 0);

    return {
      total: totalBalance.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      totalRaw: totalBalance,
    };
  }, [tokens]);
};

/**
 * Memoized token formatting utilities
 */
export const useTokenFormatting = () => {
  return useMemo(() => ({
    formatTokenBalance: (token: TokenData) => `${token.balance} ${token.symbol}`,
    
    formatUsdValue: (usdValue: string) => usdValue,
    
    formatPercentageChange: (percentageChange: string, isPositive: boolean) => {
      const sign = isPositive ? '+' : '-';
      const cleanValue = percentageChange.replace(/^[+-]/, '');
      return `${sign}${cleanValue}%`;
    },
    
    getPercentageChangeColor: (isPositive: boolean) => 
      isPositive
        ? 'var(--success-success_500, #10B981)'
        : 'var(--error-error_500, #F04438)',
  }), []);
};

/**
 * Memoized search highlighting utility
 */
export const useSearchHighlighting = (text: string, searchTerm: string) => {
  return useMemo(() => {
    if (!searchTerm.trim()) {
      return text;
    }

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }, [text, searchTerm]);
};

/**
 * Memoized transaction filtering
 */
export const useFilteredTransactions = (
  transactions: any[],
  filters: {
    types: string[];
    dateRange: { start: Date | null; end: Date | null };
    searchTerm: string;
  }
) => {
  return useMemo(() => {
    let filtered = [...transactions];

    // Filter by types
    if (filters.types.length > 0) {
      filtered = filtered.filter(tx => filters.types.includes(tx.type));
    }

    // Filter by date range
    if (filters.dateRange.start) {
      filtered = filtered.filter(tx => tx.timestamp >= filters.dateRange.start!);
    }
    if (filters.dateRange.end) {
      filtered = filtered.filter(tx => tx.timestamp <= filters.dateRange.end!);
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(tx => 
        tx.description.toLowerCase().includes(searchLower) ||
        tx.amount.includes(filters.searchTerm) ||
        tx.currency.toLowerCase().includes(searchLower) ||
        tx.transactionHash?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [transactions, filters]);
};

/**
 * Memoized component key generator for stable list rendering
 */
export const useStableKeys = <T extends { id: string }>(items: T[]) => {
  return useMemo(() => {
    return items.map(item => item.id);
  }, [items]);
};

/**
 * Performance monitoring utilities
 */
export const measurePerformance = (name: string, fn: () => void) => {
  if (process.env.NODE_ENV === 'development') {
    const start = performance.now();
    fn();
    const end = performance.now();
    console.log(`${name} took ${end - start} milliseconds`);
  } else {
    fn();
  }
};

/**
 * Memoized date formatting
 */
export const useDateFormatting = () => {
  return useMemo(() => ({
    formatDate: (date: Date) => date.toLocaleDateString(),
    formatDateTime: (date: Date) => date.toLocaleString(),
    formatRelativeTime: (date: Date) => {
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
      if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
      return `${Math.floor(diffInDays / 365)} years ago`;
    },
  }), []);
};